import { createContext, useContext, useEffect, useState, ReactNode } from 'react';

export interface TimezoneContextType {
	timezone: string | null;
	isLoading: boolean;
	error: string | null;
}

const TimezoneContext = createContext<TimezoneContextType | undefined>(undefined);

export interface TimezoneProviderProps {
	children: ReactNode;
	timezone?: string | null;
	loading?: boolean;
	error?: string | null;
}

/**
 * Generic Timezone Provider that can be configured by individual apps.
 * Apps should wrap this provider with their own GraphQL data fetching logic.
 *
 * @example
 * // In organization app:
 * <TimezoneProvider timezone={organizationSettings?.timezone} loading={loading}>
 *   <App />
 * </TimezoneProvider>
 */
export function TimezoneProvider({
	children,
	timezone = null,
	loading = false,
	error = null,
}: TimezoneProviderProps) {
	const [currentTimezone, setCurrentTimezone] = useState<string | null>(timezone);

	// Update timezone when prop changes
	useEffect(() => {
		setCurrentTimezone(timezone);
	}, [timezone]);

	const contextValue: TimezoneContextType = {
		timezone: currentTimezone,
		isLoading: loading,
		error,
	};

	return <TimezoneContext.Provider value={contextValue}>{children}</TimezoneContext.Provider>;
}

/**
 * Hook to access the current organization timezone.
 *
 * @returns TimezoneContextType containing timezone info
 * @throws Error if used outside of TimezoneProvider
 */
export function useTimezone(): TimezoneContextType {
	const context = useContext(TimezoneContext);

	if (context === undefined) {
		throw new Error('useTimezone must be used within a TimezoneProvider');
	}

	return context;
}

/**
 * Hook to get a timezone-aware date formatter.
 * Falls back to browser timezone if organization timezone is not available.
 *
 * @returns Function to format dates in the organization's timezone
 */
export function useTimezoneFormatter() {
	const { timezone, isLoading } = useTimezone();

	const formatDate = (
		date: string | Date,
		options: Intl.DateTimeFormatOptions = {},
		locale: string = 'en-US'
	): string => {
		if (!date) return '';

		const dateObj = typeof date === 'string' ? new Date(date) : date;

		if (isNaN(dateObj.getTime())) {
			return 'Invalid Date';
		}

		const formatOptions: Intl.DateTimeFormatOptions = {
			...options,
			timeZone: timezone || undefined, // Use organization timezone or browser default
		};

		try {
			return dateObj.toLocaleString(locale, formatOptions);
		} catch {
			// Fallback to basic formatting if timezone is invalid
			const fallbackOptions = { ...options };
			delete fallbackOptions.timeZone;
			return dateObj.toLocaleString(locale, fallbackOptions);
		}
	};

	return {
		/**
		 * Formats a date string or Date object using the organization's timezone
		 *
		 * @param date - Date string (ISO format) or Date object
		 * @param options - Intl.DateTimeFormatOptions
		 * @param locale - Locale string (defaults to 'en-US')
		 * @returns Formatted date string
		 */
		formatDate,

		/**
		 * Formats a date with common date format (MMM dd, yyyy)
		 */
		formatDateShort: (date: string | Date): string => {
			return formatDate(date, {
				year: 'numeric',
				month: 'short',
				day: 'numeric',
			});
		},

		/**
		 * Formats a date with date and time (MMM dd, yyyy at HH:mm)
		 */
		formatDateTime: (date: string | Date): string => {
			return formatDate(date, {
				year: 'numeric',
				month: 'short',
				day: 'numeric',
				hour: '2-digit',
				minute: '2-digit',
			});
		},

		/**
		 * Formats a date with full date and time info including timezone
		 */
		formatDateTimeFull: (date: string | Date): string => {
			return formatDate(date, {
				year: 'numeric',
				month: 'short',
				day: 'numeric',
				hour: '2-digit',
				minute: '2-digit',
				timeZoneName: 'short',
			});
		},

		isLoading,
		timezone,
	};
}
